import { Injectable } from '@angular/core';
import { delay, Observable, of } from 'rxjs';
import { HttpEvent, HttpRequest, HttpResponse } from '@angular/common/http';
import { ApiResponse } from '@app/core/adaptors/dto';
import { OverdueInvoice } from '@app/core/models/invoice.interface';
import {
  BulkDeliveryResult,
  RegisterBulkDeliveryRequest,
  RegisterBulkDeliveryResponse,
} from '@app/core/models/delivery.interface';

/**
 * Mock data for the Overdue Invoices page so it renders under `npm start` with
 * `useMockData = true`. Covers both feature endpoints: the overdue list read and
 * the bulk-send action. A couple of rows deliberately have no email to exercise
 * the "No email" chip / non-selectable behaviour.
 */
@Injectable({ providedIn: 'root' })
export class OverdueInvoicesMocksService {
  private readonly mockOverdueInvoices: OverdueInvoice[] = [
    this.buildInvoice({
      id: 501,
      invoiceNumber: 'INV-501',
      customerName: 'Tanaka Auto Services',
      vehicleRego: 'ABC123',
      total: 450.0,
      balance: 450.0,
      dueDate: '2026-08-20',
      customerEmail: 'kenji@tanakaauto.co.nz',
      daysOverdue: 20,
    }),
    this.buildInvoice({
      id: 502,
      invoiceNumber: 'INV-502',
      customerName: 'Wellington Wheels Ltd',
      vehicleRego: 'WLG555',
      total: 1280.5,
      balance: 640.25,
      dueDate: '2026-08-01',
      customerEmail: 'sarah@wellingtonwheels.co.nz',
      daysOverdue: 39,
    }),
    this.buildInvoice({
      id: 503,
      invoiceNumber: 'INV-503',
      customerName: 'Christchurch Car Care',
      vehicleRego: 'CHC900',
      total: 210.0,
      balance: 210.0,
      dueDate: null,
      customerEmail: null,
      daysOverdue: 12,
    }),
    this.buildInvoice({
      id: 504,
      invoiceNumber: 'INV-504',
      customerName: 'Harbour City Motors',
      vehicleRego: 'HCM042',
      total: 875.0,
      balance: 875.0,
      dueDate: '2026-09-01',
      customerEmail: 'accounts@harbourcitymotors.co.nz',
      daysOverdue: 8,
    }),
    this.buildInvoice({
      id: 505,
      invoiceNumber: 'INV-505',
      customerName: 'Otago Fleet Services',
      vehicleRego: 'OTG777',
      total: 320.0,
      balance: 320.0,
      dueDate: null,
      customerEmail: '',
      daysOverdue: 9,
    }),
  ];

  handleMockRequest(req: HttpRequest<any>): Observable<HttpEvent<any>> {
    if (req.method === 'GET' && req.url.includes('/invoices/overdue')) {
      return this.getOverdueInvoices();
    }

    if (req.method === 'POST' && req.url.includes('/deliveries/register-bulk')) {
      return this.registerBulk(req.body as RegisterBulkDeliveryRequest);
    }

    throw new Error(`Unsupported mock request: ${req.method} ${req.url}`);
  }

  private getOverdueInvoices(): Observable<HttpEvent<any>> {
    const sorted = [...this.mockOverdueInvoices].sort((a, b) => b.daysOverdue - a.daysOverdue);
    const res: ApiResponse<OverdueInvoice> = {
      data: sorted,
      total: sorted.length,
      page: 1,
      limit: 500,
      totalPages: 1,
    };
    return this.mapToResponse(res, 200);
  }

  private registerBulk(request: RegisterBulkDeliveryRequest): Observable<HttpEvent<any>> {
    let nextTaskId = 9000;
    const results: BulkDeliveryResult[] = (request.resourceIds ?? []).map((resourceId) => {
      const invoice = this.mockOverdueInvoices.find((inv) => inv.id === resourceId);
      if (!invoice) {
        return { resourceId, status: 'NOT_FOUND' };
      }
      const email = invoice.customerEmail;
      if (!email || email.trim().length === 0) {
        return { resourceId, status: 'SKIPPED_NO_EMAIL' };
      }
      return { resourceId, status: 'QUEUED', taskId: nextTaskId++ };
    });

    const response: RegisterBulkDeliveryResponse = {
      queuedCount: results.filter((r) => r.status === 'QUEUED').length,
      skippedCount: results.filter((r) => r.status !== 'QUEUED').length,
      results,
    };
    return this.mapToResponse(response, 201);
  }

  private buildInvoice(partial: {
    id: number;
    invoiceNumber: string;
    customerName: string;
    vehicleRego: string;
    total: number;
    balance: number;
    dueDate: string | null;
    customerEmail: string | null;
    daysOverdue: number;
  }): OverdueInvoice {
    return {
      id: partial.id,
      tenantId: 1,
      invoiceNumber: partial.invoiceNumber,
      jobId: null,
      customerId: partial.id,
      customerName: partial.customerName,
      vehicleId: null,
      vehicleRego: partial.vehicleRego,
      technicianId: null,
      technicianName: null,
      status: 'PENDING',
      subtotal: partial.total,
      gst: 0,
      total: partial.total,
      amountPaid: partial.total - partial.balance,
      balance: partial.balance,
      createdAt: '2026-08-01T09:00:00Z',
      updatedAt: '2026-08-01T09:00:00Z',
      dueDate: partial.dueDate,
      cancellationReason: null,
      cancelledAt: null,
      cancelledBy: null,
      bankedAt: null,
      bankedBy: null,
      customerEmail: partial.customerEmail,
      daysOverdue: partial.daysOverdue,
    };
  }

  private mapToResponse<T>(res: T, statusCode: number): Observable<HttpEvent<T>> {
    return of(
      new HttpResponse({
        status: statusCode,
        body: res,
      })
    ).pipe(delay(400));
  }
}
